import { useMemoizedFn } from 'ahooks';
import type { DragEvent } from 'react';

/**
 * 拖拽排序的配置选项
 */
interface UseDragAndDropOptions {
  /**
   * 拖拽完成后的回调函数
   * @param sourceIndex 源索引
   * @param targetIndex 目标索引
   */
  onReorder?: (sourceIndex: number, targetIndex: number) => void;
}

/**
 * 拖拽事件处理函数
 */
interface DragHandlers {
  /**
   * 拖拽开始事件处理函数
   * @param e 拖拽事件
   * @param sourceIndex 源索引
   */
  onDragStart: (e: DragEvent, sourceIndex: number) => void;
  /**
   * 拖拽悬停事件处理函数
   */
  onDragOver: (e: DragEvent) => void;
  /**
   * 拖拽放置事件处理函数
   * @param e 拖拽事件
   * @param targetIndex 目标索引
   */
  onDropCapture: (e: DragEvent, targetIndex: number) => void;
}

/**
 * 通用的拖拽排序 Hook
 *
 * 使用动态索引模式，在每次拖拽事件中传入索引，提供最大的灵活性
 *
 * @example
 * const { onDragStart, onDragOver, onDropCapture } = useDragAndDrop({
 *   onReorder: (source, target) => console.log(source, target)
 * });
 *
 * <div
 *   draggable
 *   onDragStart={(e) => onDragStart(e, index)}
 *   onDragOver={onDragOver}
 *   onDropCapture={(e) => onDropCapture(e, index)}
 * >
 *   Item
 * </div>
 */
export function useDragAndDrop({ onReorder }: UseDragAndDropOptions = {}): DragHandlers {
  const onDragStart = useMemoizedFn((e: DragEvent, sourceIndex: number) => {
    e.dataTransfer.setData('text/plain', sourceIndex.toString());
    e.dataTransfer.effectAllowed = 'move';
  });

  const onDragOver = useMemoizedFn((e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  });

  const onDropCapture = useMemoizedFn((e: DragEvent, targetIndex: number) => {
    e.preventDefault();

    const sourceIndex = Number.parseInt(e.dataTransfer.getData('text/plain'), 10);

    if (sourceIndex === targetIndex) return;

    if (Number.isNaN(sourceIndex) || Number.isNaN(targetIndex) || targetIndex === null || targetIndex === undefined) {
      return;
    }

    onReorder?.(sourceIndex, targetIndex);
  });

  return {
    onDragStart,
    onDragOver,
    onDropCapture,
  };
}
